// Clinic-portal API surface. Everything here is scoped server-side to the signed-in clinic's own
// doctor group — the client never sends a clinic/doctor id, so it can't ask for someone else's data.
import { prepareUpload } from '../../lib/compressUpload.ts';
import { uploadWithProgress } from '../../lib/uploadWithProgress.ts';
import type {
  CaseCompliance,
  CaseSetup,
  SetupAnnotation,
  SetupAnnotationInput,
  SetupView,
  WfBankAccount,
  WfInvoice,
  WfClinicPrefs,
  WfClinicProfile,
  WfNotice,
  WfPayment,
  WfStatement,
  WfPortalCase,
  WfPortalCaseView,
  WfPortalDashboard,
  WfPortalFile,
  WfPortalMessage,
  WfPortalMeta,
  WfPortalRequest,
  WfPortalSubmit,
  WfScanBooking,
  WfScanSlot,
} from '@alignova/shared';
import { api } from '../../lib/api.ts';

const P = '/workflow/portal';

export interface PortalCaseList {
  items: WfPortalCase[];
  total: number;
}

export const portalApi = {
  meta: () => api.get<WfPortalMeta>(`${P}/meta`),
  dashboard: () => api.get<WfPortalDashboard>(`${P}/dashboard`),
  submit: (body: WfPortalSubmit) => api.post<WfPortalCase>(`${P}/cases`, body),
  cases: (q: { search?: string; includeTerminal?: boolean; caseType?: string } = {}) => {
    const sp = new URLSearchParams();
    if (q.search) sp.set('search', q.search);
    if (q.caseType) sp.set('caseType', q.caseType);
    if (q.includeTerminal) sp.set('includeTerminal', '1');
    const s = sp.toString();
    return api.get<PortalCaseList>(`${P}/cases${s ? `?${s}` : ''}`);
  },
  case: (id: string) => api.get<WfPortalCaseView>(`${P}/cases/${id}`),
  /** The doctor's read-only view of their patient's wear record and progress photos. */
  compliance: (id: string) => api.get<CaseCompliance>(`${P}/cases/${id}/compliance`),
  /** The clinic's own invoices. */
  invoices: () => api.get<WfInvoice[]>(`${P}/invoices`),
  invoice: (id: string) => api.get<WfInvoice>(`${P}/invoices/${id}`),
  raiseRequest: (id: string, body: { kind: 'change' | 'treatment' | 'cancel'; description: string }) => api.post<WfPortalRequest>(`${P}/cases/${id}/requests`, body),
  approve: (id: string, comments?: string | null) => api.post<WfPortalCase>(`${P}/cases/${id}/approve`, { comments: comments ?? null }),
  messages: (id: string) => api.get<WfPortalMessage[]>(`${P}/cases/${id}/messages`),
  sendMessage: (id: string, body: string) => api.post<WfPortalMessage>(`${P}/cases/${id}/messages`, { body }),

  /** The 3D treatment setup waiting on this doctor, or null while the lab is still working. */
  setup: (caseId: string) => api.get<SetupView | null>(`${P}/cases/${caseId}/setup`),
  annotateSetup: (setupId: string, body: SetupAnnotationInput) => api.post<SetupAnnotation>(`${P}/setups/${setupId}/annotations`, body),
  decideSetup: (setupId: string, decision: 'approved' | 'changes_requested', comment?: string | null) =>
    api.post<CaseSetup>(`${P}/setups/${setupId}/decide`, { decision, comment: comment || null }),

  notices: () => api.get<WfNotice[]>(`${P}/notices`),
  slots: () => api.get<WfScanSlot[]>(`${P}/scan/slots`),
  bookings: () => api.get<WfScanBooking[]>(`${P}/scan/bookings`),
  book: (body: { slotId: string; patientName: string; contact?: string | null; caseId?: string | null; note?: string | null }) => api.post<WfScanBooking>(`${P}/scan/bookings`, body),
  cancelBooking: (id: string, reason: string) => api.post<{ ok: boolean }>(`${P}/scan/bookings/${id}/cancel`, { reason }),

  preferences: () => api.get<WfClinicPrefs>(`${P}/preferences`),
  savePreferences: (p: WfClinicPrefs) => api.put<WfClinicPrefs>(`${P}/preferences`, p),
  profile: () => api.get<WfClinicProfile>(`${P}/profile`),
  saveProfile: (p: Omit<WfClinicProfile, 'code'>) => api.put<WfClinicProfile>(`${P}/profile`, p),
  submitAssessment: (body: { patientFirstName: string; patientLastName?: string | null; chiefComplaint: string; phone?: string | null; notes?: string | null }) =>
    api.post<WfPortalCase>(`${P}/assessments`, body),

  // Money. The doctor group is resolved from the session, so no id is ever sent.
  statement: (range: { from?: string; to?: string } = {}) => {
    const sp = new URLSearchParams();
    if (range.from) sp.set('from', range.from);
    if (range.to) sp.set('to', range.to);
    const s = sp.toString();
    return api.get<WfStatement>(`${P}/statement${s ? `?${s}` : ''}`);
  },
  bankAccounts: () => api.get<WfBankAccount[]>(`${P}/billing/accounts`),
  payments: () => api.get<WfPayment[]>(`${P}/payments`),
};

/**
 * Submit a payment. Multipart because the proof (bank slip / screenshot) rides along — one action
 * for the doctor rather than "create, then attach". `amount` is PAISA, converted at the input.
 */
export async function submitPortalPayment(
  fields: { amount: number; method: string; bankAccountId?: string | null; reference?: string | null; paidOn: string; note?: string | null },
  proof?: File | null,
): Promise<WfPayment> {
  const fd = new FormData();
  fd.append('amount', String(fields.amount));
  fd.append('method', fields.method);
  fd.append('paidOn', fields.paidOn);
  if (fields.bankAccountId) fd.append('bankAccountId', fields.bankAccountId);
  if (fields.reference) fd.append('reference', fields.reference);
  if (fields.note) fd.append('note', fields.note);
  if (proof) fd.append('proof', proof);
  const res = await fetch(`/api${P}/payments`, { method: 'POST', credentials: 'include', body: fd });
  const data = (await res.json().catch(() => null)) as (WfPayment & { error?: string }) | null;
  if (!res.ok || !data) throw new Error(data?.error ?? `Could not submit the payment (${res.status})`);
  return data;
}

export const portalProofUrl = (paymentId: string) => `/api${P}/payments/${paymentId}/proof`;

/** Multipart upload of one record into a named slot (the JSON wrapper can't do FormData). */
export async function uploadPortalRecord(caseId: string, file: File, slot?: string, onProgress?: (pct: number) => void): Promise<WfPortalFile | PortalArchiveOutcome> {
  const fd = new FormData();
  // The clinic's uplink is usually the worst one in the chain, so this is where it matters most.
  const prepared = await prepareUpload(file);
  fd.append('file', prepared.body, file.name);
  if (prepared.gzipped) fd.append('gz', '1');
  if (slot) fd.append('slot', slot);
  return uploadWithProgress<WfPortalFile | PortalArchiveOutcome>(`/api${P}/cases/${caseId}/files`, fd, onProgress);
}
/** A .zip/.rar upload is extracted server-side into individual records. */
export interface PortalArchiveOutcome {
  archive: string;
  extracted: Array<{ id: string; originalName: string }>;
  skipped: Array<{ name: string; reason: string }>;
  unavailable?: boolean;
}
export const isPortalArchiveOutcome = (o: WfPortalFile | PortalArchiveOutcome): o is PortalArchiveOutcome => 'archive' in o;

export const portalFileUrl = (caseId: string, fileId: string) => `/api${P}/cases/${caseId}/files/${fileId}/download`;
/** Same file, streamed in place — what a <video> or <img> src needs (the server honours Range). */
export const portalInlineUrl = (caseId: string, fileId: string) => `/api${P}/cases/${caseId}/files/${fileId}/download?inline=1`;
